import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeOutlined";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";

import { useAdvisor } from "src/advisor/AdvisorProvider";
import { usePermissions } from "src/auth/usePermissions";
import { useSession } from "src/auth/useSession";
import { SIDE_NAV_WIDTH, TOP_BAR_HEIGHT } from "src/components/AppShell/layout";
import { NAV_ITEMS, type NavItem, SECONDARY_NAV_ITEMS } from "src/components/AppShell/navItems";
import { useSolutions } from "src/components/Solutions/useSolutions";
import { useUnreadNotifications } from "src/notifications/useNotifications";

const ICON_SIZE = 20;
const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);

/** A count pill ("My Solutions 3"): one of the few filled elements in the design. */
const NavCount: FC<{ value: number }> = ({ value }) => (
	<Box
		component="span"
		sx={{
			px: 0.75,
			borderRadius: "10px",
			bgcolor: "brand.count",
			color: "primary.main",
			fontSize: 12,
			fontWeight: 600,
			lineHeight: "18px"
		}}>
		{value}
	</Box>
);

interface NavListProps {
	items: readonly NavItem[];
	/** The count pill per item ("My Solutions 3", "Activity 4"); none at zero. */
	counts?: Partial<Record<NavItem["key"], number>>;
}

const NavList: FC<NavListProps> = ({ items, counts = {} }) => {
	const { t } = useTranslation();
	return (
		<List disablePadding sx={{ display: "grid", gap: 0.5 }}>
			{items.map(({ key, to, icon: Icon, end }) => (
				<ListItemButton key={key} component={NavLink} to={to} end={end}>
					<ListItemIcon>
						<Icon sx={{ fontSize: ICON_SIZE }} />
					</ListItemIcon>
					<ListItemText primary={t(`nav.${key}`)} />
					{Boolean(counts[key]) && <NavCount value={counts[key] ?? 0} />}
				</ListItemButton>
			))}
		</List>
	);
};

/** Desktop navigation (Figma "Nav · pinned"): destinations on top, the AI Advisor pinned to the bottom. */
export const SideNav: FC = () => {
	const { available: advisorAvailable, openAdvisor } = useAdvisor();
	const { t } = useTranslation();
	const solutions = useSolutions();
	const unread = useUnreadNotifications();
	const { canReadNotifications } = usePermissions();
	const items = canReadNotifications ? NAV_ITEMS : NAV_ITEMS.filter(item => item.key !== "activity");
	const counts = { solutions: solutions.data?.length ?? 0, activity: unread.data?.unread ?? 0 };
	// Companies are a partner's customers; a prospect only ever has their own.
	const { isPartner } = useSession();

	return (
		<Box
			component="nav"
			aria-label={t("nav.primary")}
			sx={{
				display: { xs: "none", md: "flex" },
				flexDirection: "column",
				flexShrink: 0,
				width: SIDE_NAV_WIDTH,
				position: "sticky",
				top: TOP_BAR_HEIGHT.md,
				height: `calc(100dvh - ${TOP_BAR_HEIGHT.md}px)`,
				px: 1,
				py: 1.5,
				bgcolor: "background.paper",
				borderRight: 1,
				borderColor: "divider"
			}}>
			<NavList items={items} counts={counts} />
			<Box sx={{ flex: 1 }} />
			{isPartner && (
				<>
					<Divider sx={{ mb: 1 }} />
					<NavList items={SECONDARY_NAV_ITEMS} />
				</>
			)}
			{advisorAvailable && <Divider sx={{ mb: 1 }} />}
			{advisorAvailable && (
				<ListItemButton selected onClick={openAdvisor} sx={{ flexGrow: 0 }}>
					<ListItemIcon>
						<AutoAwesomeIcon sx={{ fontSize: ICON_SIZE }} />
					</ListItemIcon>
					<ListItemText primary={t("nav.advisor")} />
					<Typography component="kbd" variant="caption" color="text.secondary" sx={{ fontFamily: "inherit" }}>
						{IS_MAC ? "⌘K" : "Ctrl K"}
					</Typography>
				</ListItemButton>
			)}
		</Box>
	);
};
